import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { CONSOLIDATION } from "@/constants/testIds";
import {
  Plus,
  Search,
  Trash2,
  ChevronRight,
  ListChecks,
  AlertCircle,
  CheckCircle2,
  X,
  FileDown,
  Heart,
  UserPlus,
  Users,
  Award,
  TrendingUp,
  Droplets,
  MessageCircle,
  Send,
} from "lucide-react";

const STAGES = [
  { key: "visitor", label: "Visitante", color: "#8b7e5e" },
  { key: "convert", label: "Converso", color: "#7a5b0f" },
  { key: "baptized", label: "Bautismo", color: "#1e6f8a" },
  { key: "retreat", label: "Retiro", color: "#5a4a8a" },
  { key: "discipleship", label: "Discipulado", color: "#8a4a5a" },
  { key: "academy", label: "Academia", color: "#1c2a3a" },
  { key: "cell_leader", label: "Líder de Célula", color: "#0f7a4a" },
];

const STAGE_LABEL = STAGES.reduce((acc, s) => ({ ...acc, [s.key]: s.label }), {});
const STAGE_COLOR = STAGES.reduce((acc, s) => ({ ...acc, [s.key]: s.color }), {});
const STAGE_INDEX = STAGES.reduce((acc, s, i) => ({ ...acc, [s.key]: i }), {});

const TABS = [
  { key: "visitors", label: "Visitantes", icon: UserPlus, testid: CONSOLIDATION.tabVisitors },
  { key: "converts", label: "Conversos", icon: Heart, testid: CONSOLIDATION.tabConverts },
  { key: "followup", label: "Seguimiento", icon: ListChecks, testid: CONSOLIDATION.tabFollowup },
  { key: "ladder", label: "Escalera del Discípulo", icon: TrendingUp, testid: CONSOLIDATION.tabLadder },
  { key: "baptisms", label: "Bautismos", icon: Droplets, testid: CONSOLIDATION.tabBaptisms },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function Consolidation() {
  const [tab, setTab] = useState("visitors");
  const [persons, setPersons] = useState([]);
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Modal state
  const [editing, setEditing] = useState(null); // person object or "new"
  const [advancing, setAdvancing] = useState(null); // person
  const [followupPerson, setFollowupPerson] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [pRes, cRes] = await Promise.all([
        api.get("/consolidation/persons"),
        api.get("/cells"),
      ]);
      setPersons(pRes.data || []);
      setCells(cRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const flash = (msg) => {
    setInfo(msg);
    setError("");
    setTimeout(() => setInfo(""), 2500);
  };

  const cellName = (id) => cells.find((c) => c.id === id)?.name || "";

  // --- Mutations ---

  const savePerson = async (payload, id) => {
    try {
      if (id) {
        await api.patch(`/consolidation/persons/${id}`, payload);
      } else {
        await api.post("/consolidation/persons", payload);
      }
      setEditing(null);
      flash(id ? "Persona actualizada" : "Visitante agregado");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/consolidation/persons/${confirmDelete.id}`);
      setConfirmDelete(null);
      flash("Persona eliminada");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const advance = async (payload) => {
    if (!advancing) return;
    try {
      await api.post(`/consolidation/persons/${advancing.id}/advance`, payload);
      setAdvancing(null);
      flash(`Avanzado a ${STAGE_LABEL[payload.stage]}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al avanzar etapa");
    }
  };

  const toggleTask = async (personId, taskKey, completed, notes) => {
    // Optimistic UI update — flip state immediately so the checkbox feels instant
    const today_iso = new Date().toISOString().slice(0, 10);
    const patchPerson = (p) => {
      if (!p || p.id !== personId) return p;
      const tasks = (p.tasks || []).map((t) =>
        t.key === taskKey
          ? {
              ...t,
              completed,
              completed_date: completed ? today_iso : "",
              notes: notes ?? t.notes ?? "",
            }
          : t,
      );
      return { ...p, tasks };
    };
    setPersons((prev) => prev.map(patchPerson));
    setFollowupPerson((prev) => patchPerson(prev));

    try {
      const res = await api.patch(
        `/consolidation/persons/${personId}/tasks/${taskKey}`,
        { completed, notes: notes ?? "" },
      );
      // Sync with server response (authoritative)
      setPersons((prev) => prev.map((p) => (p.id === personId ? res.data : p)));
      setFollowupPerson((prev) => (prev && prev.id === personId ? res.data : prev));
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al actualizar tarea");
      // Revert by reloading from server
      await load();
    }
  };

  const sendGreeting = async (person) => {
    try {
      const { data } = await api.post(
        `/consolidation/persons/${person.id}/send-greeting`,
      );
      // Update the person doc in state with the new greeting metadata
      setPersons((prev) =>
        prev.map((p) =>
          p.id === person.id ? { ...p, greeting: data.greeting } : p,
        ),
      );
      if (data.configured) {
        setInfo(`SMS enviado a ${data.greeting.to}`);
      } else {
        setInfo(
          `Saludo simulado a ${data.greeting.to} (configure Twilio en .env para envíos reales)`,
        );
      }
      setError("");
      setTimeout(() => setInfo(""), 4000);
    } catch (e) {
      setError(
        e?.response?.data?.detail || "Error al enviar saludo pastoral",
      );
    }
  };

  // --- Stats ---
  const stats = useMemo(() => {
    const s = { total: persons.length, visitors: 0, converts: 0, baptized: 0, leaders: 0 };
    persons.forEach((p) => {
      const idx = STAGE_INDEX[p.stage] ?? 0;
      if (p.stage === "visitor") s.visitors += 1;
      if (idx >= STAGE_INDEX.convert) s.converts += 1;
      if (idx >= STAGE_INDEX.baptized) s.baptized += 1;
      if (p.stage === "cell_leader") s.leaders += 1;
    });
    return s;
  }, [persons]);

  return (
    <div data-testid={CONSOLIDATION.page}>
      <PageHeader
        title="Consolidación y Discipulado"
        subtitle="Acompañamiento del visitante al líder · Escalera del discípulo"
      />

      <div className="grid grid-cols-4 gap-4 px-8 pt-6">
        <Stat icon={UserPlus} label="Total personas" value={stats.total} />
        <Stat icon={Heart} label="Visitantes activos" value={stats.visitors} gold />
        <Stat icon={Droplets} label="Bautizados" value={stats.baptized} />
        <Stat icon={Award} label="Líderes de célula" value={stats.leaders} gold />
      </div>

      {/* Tabs */}
      <div className="px-8 pt-6 border-b border-[#d9cfb8]">
        <div className="flex flex-wrap gap-1">
          {TABS.map(({ key, label, icon: Icon, testid }) => (
            <button
              key={key}
              data-testid={testid}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-[12px] uppercase tracking-widest border-b-2 transition ${
                tab === key
                  ? "border-[#c8a04a] text-[#7a5b0f]"
                  : "border-transparent text-[#5d5849] hover:text-[#1c1a14]"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mx-8 mt-4 flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {info && !error && (
        <div
          data-testid="consolidation-info"
          className="mx-8 mt-4 flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700"
        >
          <CheckCircle2 size={14} /> {info}
        </div>
      )}

      <div className="p-8">
        {loading ? (
          <div className="text-center text-[#8b7e5e] py-12">Cargando...</div>
        ) : (
          <>
            {tab === "visitors" && (
              <PersonsTable
                persons={persons}
                cells={cells}
                cellName={cellName}
                filter={(p) => p.stage === "visitor"}
                title="Visitantes (etapa inicial)"
                onAdd={() => setEditing("new")}
                onEdit={(p) => setEditing(p)}
                onDelete={(p) => setConfirmDelete(p)}
                onAdvance={(p) => setAdvancing(p)}
                onFollowup={(p) => setFollowupPerson(p)}
                onSendGreeting={sendGreeting}
              />
            )}
            {tab === "converts" && (
              <PersonsTable
                persons={persons}
                cells={cells}
                cellName={cellName}
                filter={(p) => (STAGE_INDEX[p.stage] ?? 0) >= STAGE_INDEX.convert}
                title="Conversos y etapas superiores"
                onAdd={() => setEditing("new")}
                onEdit={(p) => setEditing(p)}
                onDelete={(p) => setConfirmDelete(p)}
                onAdvance={(p) => setAdvancing(p)}
                onFollowup={(p) => setFollowupPerson(p)}
                onSendGreeting={sendGreeting}
              />
            )}
            {tab === "followup" && (
              <FollowupGrid
                persons={persons}
                cellName={cellName}
                onOpen={(p) => setFollowupPerson(p)}
              />
            )}
            {tab === "ladder" && <LadderView />}
            {tab === "baptisms" && <BaptismsTab />}
          </>
        )}
      </div>

      {/* Modals */}
      {editing && (
        <PersonFormModal
          person={editing === "new" ? null : editing}
          cells={cells}
          onClose={() => setEditing(null)}
          onSave={savePerson}
        />
      )}
      {advancing && (
        <AdvanceModal
          person={advancing}
          onClose={() => setAdvancing(null)}
          onSave={advance}
        />
      )}
      {followupPerson && (
        <FollowupModal
          person={followupPerson}
          cellName={cellName(followupPerson.cell_id)}
          onClose={() => setFollowupPerson(null)}
          onToggle={toggleTask}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          title={`Eliminar a "${confirmDelete.full_name}"`}
          message="Se eliminará el registro completo (historial, tareas y bautismo). Esta acción no se puede deshacer."
          onCancel={() => setConfirmDelete(null)}
          onConfirm={doDelete}
        />
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function Stat({ icon: Icon, label, value, gold }) {
  return (
    <div className="border border-[#d9cfb8] bg-white px-4 py-3 flex items-center gap-3">
      <div
        className={`flex h-9 w-9 items-center justify-center rounded ${
          gold ? "bg-[#fbf7ee] text-[#7a5b0f]" : "bg-[#eef2f7] text-[#1c2a3a]"
        }`}
      >
        <Icon size={18} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">{label}</div>
        <div className="font-serif text-xl tabular-nums text-[#1c1a14]">{value}</div>
      </div>
    </div>
  );
}

function StageBadge({ stage }) {
  const color = STAGE_COLOR[stage] || "#8b7e5e";
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-widest text-white"
      style={{ background: color }}
    >
      {STAGE_LABEL[stage] || stage}
    </span>
  );
}

function PersonsTable({
  persons,
  cellName,
  filter,
  title,
  onAdd,
  onEdit,
  onDelete,
  onAdvance,
  onFollowup,
  onSendGreeting,
}) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return persons
      .filter(filter)
      .filter((p) => !q || p.full_name.toLowerCase().includes(q));
  }, [persons, filter, search]);

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#8b7e5e]">{title}</div>
          <div className="text-xs text-[#5d5849]">{filtered.length} registro(s)</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8b7e5e]"
            />
            <input
              data-testid={CONSOLIDATION.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre..."
              className="border border-[#d9cfb8] bg-white pl-7 pr-3 py-2 text-[12px] rounded w-56"
            />
          </div>
          <button
            data-testid={CONSOLIDATION.addButton}
            onClick={onAdd}
            className="inline-flex items-center gap-2 rounded border border-[#1c2a3a] bg-[#1c2a3a] px-3 py-2 text-[11px] uppercase tracking-widest text-white hover:bg-[#0f1a2a]"
          >
            <Plus size={14} /> Nuevo
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-[#d9cfb8] bg-white shadow-sm">
        <table data-testid={CONSOLIDATION.table} className="w-full text-[13px]">
          <thead className="bg-[#1c2a3a] text-white">
            <tr>
              <Th>#</Th>
              <Th>Nombre</Th>
              <Th>Teléfono</Th>
              <Th>Primera visita</Th>
              <Th>Invitado por</Th>
              <Th>Célula</Th>
              <Th center>Etapa</Th>
              <Th center>Tareas</Th>
              <Th center>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-[#8b7e5e]">
                  No hay registros. {onAdd && 'Usa "Nuevo" para empezar.'}
                </td>
              </tr>
            ) : (
              filtered.map((p, i) => {
                const tasksDone = (p.tasks || []).filter((t) => t.completed).length;
                const tasksTotal = (p.tasks || []).length;
                return (
                  <tr
                    key={p.id}
                    data-testid={CONSOLIDATION.row(p.id)}
                    className="border-t border-[#e8e1cd] hover:bg-[#fbf7ee]"
                  >
                    <td className="px-3 py-2 text-[11px] tabular-nums text-[#8b7e5e]">
                      {i + 1}
                    </td>
                    <td className="px-3 py-2 text-[#1c1a14]">{p.full_name}</td>
                    <td className="px-3 py-2 text-[#5d5849]">{p.phone || "—"}</td>
                    <td className="px-3 py-2 tabular-nums text-[#5d5849]">
                      {p.first_visit_date || "—"}
                    </td>
                    <td className="px-3 py-2 text-[#5d5849]">{p.invited_by || "—"}</td>
                    <td className="px-3 py-2 text-[#5d5849]">
                      {cellName(p.cell_id) || "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <StageBadge stage={p.stage} />
                    </td>
                    <td className="px-3 py-2 text-center tabular-nums text-[#1c1a14]">
                      {tasksDone}/{tasksTotal}
                    </td>
                    <td className="px-3 py-2">
                      <div className="inline-flex items-center justify-center gap-3">
                        <button
                          data-testid={CONSOLIDATION.followupButton(p.id)}
                          onClick={() => onFollowup(p)}
                          className="text-[#1c2a3a] hover:text-[#c8a04a]"
                          title="Seguimiento (checklist)"
                        >
                          <ListChecks size={14} />
                        </button>
                        <button
                          data-testid={CONSOLIDATION.advanceButton(p.id)}
                          onClick={() => onAdvance(p)}
                          className="text-[#7a5b0f] hover:text-[#c8a04a]"
                          title="Avanzar etapa"
                        >
                          <ChevronRight size={16} />
                        </button>
                        <GreetingButton person={p} onSend={onSendGreeting} />
                        <button
                          data-testid={CONSOLIDATION.editButton(p.id)}
                          onClick={() => onEdit(p)}
                          className="text-[#1c2a3a] hover:text-[#c8a04a] text-[11px] uppercase tracking-widest"
                        >
                          Editar
                        </button>
                        <button
                          data-testid={CONSOLIDATION.deleteButton(p.id)}
                          onClick={() => onDelete(p)}
                          className="text-[#9b2c2c] hover:text-[#6e1f1f]"
                          title="Eliminar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function FollowupGrid({ persons, cellName, onOpen }) {
  // Only show persons still in pre-baptism follow-up (visitor or convert)
  const tracked = persons.filter(
    (p) => (STAGE_INDEX[p.stage] ?? 0) <= STAGE_INDEX.baptized,
  );

  if (tracked.length === 0) {
    return (
      <div className="text-center text-[#8b7e5e] py-12">
        No hay visitantes o conversos en seguimiento.
      </div>
    );
  }

  return (
    <>
      <div className="text-[11px] uppercase tracking-widest text-[#8b7e5e] mb-4">
        Seguimiento del líder — Checklist 1 al 5
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tracked.map((p) => {
          const done = (p.tasks || []).filter((t) => t.completed).length;
          const total = (p.tasks || []).length || 5;
          const pct = total ? Math.round((done / total) * 100) : 0;
          return (
            <button
              key={p.id}
              data-testid={CONSOLIDATION.followupCard(p.id)}
              onClick={() => onOpen(p)}
              className="text-left border border-[#d9cfb8] bg-white p-4 hover:border-[#c8a04a] transition"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-serif text-[16px] text-[#1c1a14]">{p.full_name}</div>
                  <div className="text-[11px] text-[#8b7e5e] mt-0.5">
                    {cellName(p.cell_id) || "Sin célula"} · {p.phone || "—"}
                  </div>
                </div>
                <StageBadge stage={p.stage} />
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="uppercase tracking-widest text-[#8b7e5e]">
                    Avance
                  </span>
                  <span className="tabular-nums text-[#1c1a14]">
                    {done}/{total}
                  </span>
                </div>
                <div className="h-2 w-full bg-[#f5f0e6] rounded overflow-hidden">
                  <div
                    className="h-2 bg-[#c8a04a]"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(p.tasks || []).map((t) => (
                  <span
                    key={t.key}
                    title={t.label}
                    className={`text-[10px] px-1.5 py-0.5 rounded border ${
                      t.completed
                        ? "bg-[#0f7a4a] text-white border-[#0f7a4a]"
                        : "bg-white text-[#8b7e5e] border-[#d9cfb8]"
                    }`}
                  >
                    {t.label.split(".")[0]}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function LadderView() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/consolidation/ladder")
      .then((res) => setData(res.data))
      .catch((e) => setError(e?.response?.data?.detail || "Error cargando escalera"));
  }, []);

  if (error) {
    return <div className="text-red-700 text-[13px]">{error}</div>;
  }
  if (!data) {
    return <div className="text-center text-[#8b7e5e] py-12">Cargando escalera...</div>;
  }

  const maxCount = Math.max(1, ...data.stages.map((s) => s.count));

  return (
    <>
      <div className="text-[11px] uppercase tracking-widest text-[#8b7e5e] mb-4">
        Escalera del Discípulo — visualización tipo embudo
      </div>
      <div className="space-y-2 max-w-3xl">
        {data.stages.map((s) => {
          const widthPct = Math.max(8, Math.round((s.count / maxCount) * 100));
          return (
            <div
              key={s.stage}
              data-testid={CONSOLIDATION.ladderColumn(s.stage)}
              className="flex items-center gap-3"
            >
              <div className="w-40 text-right text-[12px] uppercase tracking-widest text-[#5d5849]">
                {s.label}
              </div>
              <div className="flex-1 h-9 bg-[#f5f0e6] rounded">
                <div
                  className="h-9 rounded flex items-center justify-end px-3 text-white text-[13px] tabular-nums"
                  style={{
                    width: `${widthPct}%`,
                    background: STAGE_COLOR[s.stage] || "#8b7e5e",
                  }}
                >
                  <span data-testid={CONSOLIDATION.ladderCount(s.stage)}>{s.count}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 text-[11px] uppercase tracking-widest text-[#8b7e5e]">
        Detalle por etapa
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
        {data.stages.map((s) => (
          <div key={s.stage} className="border border-[#d9cfb8] bg-white p-3">
            <div className="flex items-center justify-between">
              <StageBadge stage={s.stage} />
              <span className="text-[13px] tabular-nums text-[#1c1a14]">{s.count}</span>
            </div>
            <ul className="mt-2 space-y-0.5">
              {(s.persons || []).slice(0, 6).map((p) => (
                <li key={p.id} className="text-[12px] text-[#5d5849] truncate">
                  · {p.full_name}
                </li>
              ))}
              {s.count > 6 && (
                <li className="text-[11px] text-[#8b7e5e]">
                  + {s.count - 6} más…
                </li>
              )}
              {s.count === 0 && (
                <li className="text-[11px] text-[#8b7e5e] italic">Sin personas</li>
              )}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
}

function BaptismsTab() {
  const startDefault = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 12);
    return d.toISOString().slice(0, 10);
  };
  const [from, setFrom] = useState(startDefault());
  const [to, setTo] = useState(today());
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/consolidation/baptisms/report", {
        params: { date_from: from, date_to: to },
      });
      setReport(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error generando reporte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPdf = async () => {
    setError("");
    try {
      const res = await api.get(`/consolidation/baptisms/report.pdf`, {
        params: { date_from: from, date_to: to },
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `bautismos_${from}_a_${to}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error al descargar PDF");
    }
  };

  return (
    <>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
              Desde
            </label>
            <input
              type="date"
              data-testid={CONSOLIDATION.baptismsFromInput}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
              Hasta
            </label>
            <input
              type="date"
              data-testid={CONSOLIDATION.baptismsToInput}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            />
          </div>
          <button
            data-testid={CONSOLIDATION.baptismsGenerate}
            onClick={generate}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded border border-[#1c2a3a] bg-[#1c2a3a] px-3 py-2 text-[11px] uppercase tracking-widest text-white hover:bg-[#0f1a2a] disabled:opacity-50"
          >
            {loading ? "Generando..." : "Generar"}
          </button>
        </div>
        <button
          data-testid={CONSOLIDATION.baptismsPdfButton}
          onClick={downloadPdf}
          className="inline-flex items-center gap-2 rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-2 text-[11px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a]"
        >
          <FileDown size={14} /> Descargar PDF
        </button>
      </div>

      {error && (
        <div className="mb-3 flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {report && (
        <>
          <div
            data-testid={CONSOLIDATION.baptismsTotal}
            className="text-[12px] text-[#5d5849] mb-3"
          >
            <strong>Total de bautismos:</strong> {report.total}
          </div>

          <div className="overflow-x-auto border border-[#d9cfb8] bg-white shadow-sm">
            <table data-testid={CONSOLIDATION.baptismsTable} className="w-full text-[13px]">
              <thead className="bg-[#1c2a3a] text-white">
                <tr>
                  <Th>Fecha</Th>
                  <Th>Nombre</Th>
                  <Th>Célula</Th>
                  <Th>Oficiante</Th>
                  <Th>Lugar</Th>
                  <Th>Notas</Th>
                </tr>
              </thead>
              <tbody>
                {report.rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-[#8b7e5e]">
                      No hay bautismos en este rango.
                    </td>
                  </tr>
                ) : (
                  report.rows.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={CONSOLIDATION.baptismsRow(r.id)}
                      className="border-t border-[#e8e1cd]"
                    >
                      <td className="px-3 py-2 tabular-nums text-[#5d5849]">{r.date}</td>
                      <td className="px-3 py-2 text-[#1c1a14]">{r.full_name}</td>
                      <td className="px-3 py-2 text-[#5d5849]">{r.cell_name || "—"}</td>
                      <td className="px-3 py-2 text-[#5d5849]">{r.officiant}</td>
                      <td className="px-3 py-2 text-[#5d5849]">{r.location}</td>
                      <td className="px-3 py-2 text-[#5d5849]">{r.notes || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <BreakdownCard title="Por mes" data={report.by_month} />
            <BreakdownCard title="Por célula" data={report.by_cell} />
            <BreakdownCard title="Por oficiante" data={report.by_officiant} />
          </div>
        </>
      )}
    </>
  );
}

function BreakdownCard({ title, data }) {
  const entries = Object.entries(data || {}).sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <div className="border border-[#d9cfb8] bg-white p-4">
      <div className="text-[11px] uppercase tracking-widest text-[#8b7e5e] mb-2">
        {title}
      </div>
      {entries.length === 0 ? (
        <div className="text-[12px] text-[#8b7e5e] italic">Sin datos</div>
      ) : (
        <ul className="space-y-1">
          {entries.map(([k, v]) => (
            <li key={k} className="flex items-center justify-between text-[12px]">
              <span className="text-[#5d5849]">{k}</span>
              <span className="tabular-nums text-[#1c1a14]">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Modals ----------

function PersonFormModal({ person, cells, onClose, onSave }) {
  const isNew = !person;
  const [form, setForm] = useState({
    full_name: person?.full_name || "",
    phone: person?.phone || "",
    address: person?.address || "",
    first_visit_date: person?.first_visit_date || today(),
    invited_by: person?.invited_by || "",
    cell_id: person?.cell_id || "",
    notes: person?.notes || "",
  });
  const [error, setError] = useState("");

  const submit = (e) => {
    e?.preventDefault();
    if (!form.full_name.trim()) {
      setError("El nombre completo es obligatorio");
      return;
    }
    if (!form.first_visit_date) {
      setError("La fecha de primera visita es obligatoria");
      return;
    }
    onSave({ ...form, full_name: form.full_name.trim() }, person?.id);
  };

  return (
    <Modal testid={CONSOLIDATION.formModal} title={isNew ? "Nuevo visitante" : "Editar persona"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Nombre completo *">
          <input
            data-testid={CONSOLIDATION.fullNameInput}
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className={inputCls}
            autoFocus
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Teléfono">
            <input
              data-testid={CONSOLIDATION.phoneInput}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="Primera visita *">
            <input
              type="date"
              data-testid={CONSOLIDATION.firstVisitInput}
              value={form.first_visit_date}
              onChange={(e) => setForm({ ...form, first_visit_date: e.target.value })}
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Dirección">
          <input
            data-testid={CONSOLIDATION.addressInput}
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className={inputCls}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Invitado por">
            <input
              data-testid={CONSOLIDATION.invitedByInput}
              value={form.invited_by}
              onChange={(e) => setForm({ ...form, invited_by: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="Célula">
            <select
              data-testid={CONSOLIDATION.cellSelect}
              value={form.cell_id}
              onChange={(e) => setForm({ ...form, cell_id: e.target.value })}
              className={inputCls}
            >
              <option value="">— Sin célula —</option>
              {cells.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Notas">
          <textarea
            data-testid={CONSOLIDATION.notesInput}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            className={inputCls}
          />
        </Field>

        {error && (
          <div
            data-testid={CONSOLIDATION.errorMsg}
            className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700"
          >
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid={CONSOLIDATION.cancelButton}
            onClick={onClose}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid={CONSOLIDATION.saveButton}
            className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a]"
          >
            Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AdvanceModal({ person, onClose, onSave }) {
  const curIdx = STAGE_INDEX[person.stage] ?? 0;
  const options = STAGES.slice(curIdx + 1);
  const [stage, setStage] = useState(options[0]?.key || "");
  const [date, setDate] = useState(today());
  const [notes, setNotes] = useState("");
  const [officiant, setOfficiant] = useState("");
  const [location, setLocation] = useState("");
  const [error, setError] = useState("");

  if (options.length === 0) {
    return (
      <Modal testid={CONSOLIDATION.advanceModal} title="Avanzar etapa" onClose={onClose}>
        <p className="text-[13px] text-[#5d5849]">
          <strong>{person.full_name}</strong> ya está en la etapa más alta (
          {STAGE_LABEL[person.stage]}).
        </p>
        <div className="flex justify-end pt-3">
          <button
            onClick={onClose}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cerrar
          </button>
        </div>
      </Modal>
    );
  }

  const submit = (e) => {
    e.preventDefault();
    if (!stage) {
      setError("Seleccione una etapa");
      return;
    }
    onSave({
      stage,
      date,
      notes,
      officiant: stage === "baptized" ? officiant : "",
      location: stage === "baptized" ? location : "",
    });
  };

  return (
    <Modal
      testid={CONSOLIDATION.advanceModal}
      title={`Avanzar etapa — ${person.full_name}`}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-3">
        <p className="text-[12px] text-[#5d5849]">
          Etapa actual: <StageBadge stage={person.stage} />
        </p>
        <Field label="Nueva etapa">
          <select
            data-testid={CONSOLIDATION.advanceStageSelect}
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className={inputCls}
          >
            {options.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Fecha">
          <input
            type="date"
            data-testid={CONSOLIDATION.advanceDateInput}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={inputCls}
          />
        </Field>
        {stage === "baptized" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Oficiante">
              <input
                data-testid={CONSOLIDATION.advanceOfficiantInput}
                value={officiant}
                onChange={(e) => setOfficiant(e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Lugar">
              <input
                data-testid={CONSOLIDATION.advanceLocationInput}
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
        )}
        <Field label="Notas">
          <textarea
            data-testid={CONSOLIDATION.advanceNotesInput}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className={inputCls}
          />
        </Field>

        {error && (
          <div className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid={CONSOLIDATION.advanceConfirm}
            className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a]"
          >
            Avanzar
          </button>
        </div>
      </form>
    </Modal>
  );
}

function FollowupModal({ person, cellName, onClose, onToggle }) {
  return (
    <Modal
      testid={CONSOLIDATION.followupModal}
      title="Seguimiento del discípulo"
      onClose={onClose}
      wide
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div
            data-testid={CONSOLIDATION.followupPersonName}
            className="font-serif text-lg text-[#1c1a14]"
          >
            {person.full_name}
          </div>
          <div className="text-[11px] text-[#8b7e5e]">
            {cellName || "Sin célula"} · Etapa: <StageBadge stage={person.stage} />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {(person.tasks || []).map((t) => (
          <div
            key={t.key}
            className={`border p-3 rounded ${
              t.completed ? "border-[#0f7a4a] bg-[#f0fdf4]" : "border-[#d9cfb8] bg-white"
            }`}
          >
            <div className="flex items-start gap-3">
              <label className="flex items-center cursor-pointer pt-0.5 select-none">
                <input
                  type="checkbox"
                  data-testid={CONSOLIDATION.taskCheckbox(t.key)}
                  checked={!!t.completed}
                  onChange={(e) => onToggle(person.id, t.key, e.target.checked, t.notes)}
                  className="h-4 w-4 accent-[#0f7a4a] cursor-pointer"
                />
              </label>
              <div className="flex-1">
                <div className="text-[13px] text-[#1c1a14] font-medium">{t.label}</div>
                {t.completed && t.completed_date && (
                  <div className="text-[11px] text-[#0f7a4a] tabular-nums">
                    Completado: {t.completed_date}
                  </div>
                )}
                <input
                  data-testid={CONSOLIDATION.taskNotesInput(t.key)}
                  placeholder="Notas (opcional)"
                  defaultValue={t.notes || ""}
                  onBlur={(e) => {
                    if ((t.notes || "") !== e.target.value) {
                      onToggle(person.id, t.key, !!t.completed, e.target.value);
                    }
                  }}
                  className="mt-2 w-full border border-[#d9cfb8] bg-white px-2 py-1 text-[12px] rounded"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end pt-4">
        <button
          data-testid={CONSOLIDATION.followupClose}
          onClick={onClose}
          className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
        >
          Cerrar
        </button>
      </div>
    </Modal>
  );
}

function ConfirmModal({ title, message, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded border border-[#d9cfb8] bg-white p-6 shadow-xl">
        <div className="text-base font-medium text-[#1c1a14]">{title}</div>
        <div className="mt-2 text-[13px] text-[#5b5240]">{message}</div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="rounded border border-red-700 bg-red-700 px-3 py-1.5 text-[12px] uppercase tracking-widest text-white hover:bg-red-800"
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        data-testid={testid}
        className={`w-full ${wide ? "max-w-2xl" : "max-w-lg"} rounded border border-[#d9cfb8] bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-[#1c1a14]">{title}</h3>
          <button onClick={onClose} className="text-[#8b7e5e] hover:text-[#1c1a14]">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function Th({ children, center }) {
  return (
    <th
      className={`px-3 py-2 text-[10px] uppercase tracking-widest font-medium ${
        center ? "text-center" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function GreetingButton({ person, onSend }) {
  const greeting = person.greeting;
  const hasPhone = !!person.phone;
  const status = greeting?.status; // sent | simulated | failed | undefined

  let icon = <Send size={14} />;
  let color = "text-[#0f7a4a] hover:text-[#0a5a35]";
  let title = "Enviar saludo pastoral (SMS)";

  if (!hasPhone) {
    color = "text-[#d4cfbe] cursor-not-allowed";
    title = "Sin teléfono registrado";
  } else if (status === "sent") {
    icon = <CheckCircle2 size={14} />;
    color = "text-[#0f7a4a] hover:text-[#0a5a35]";
    title = `Enviado a ${greeting.to} · Clic para reenviar`;
  } else if (status === "simulated") {
    icon = <MessageCircle size={14} />;
    color = "text-[#7a5b0f] hover:text-[#c8a04a]";
    title = `Simulado (Twilio no configurado) · Clic para reintentar`;
  } else if (status === "failed") {
    icon = <AlertCircle size={14} />;
    color = "text-[#9b2c2c] hover:text-[#6e1f1f]";
    title = `Falló: ${greeting?.detail || "error"} · Clic para reintentar`;
  }

  return (
    <button
      data-testid={CONSOLIDATION.greetingButton(person.id)}
      data-greeting-status={status || "none"}
      onClick={() => hasPhone && onSend(person)}
      disabled={!hasPhone}
      className={`${color} text-[11px]`}
      title={title}
    >
      {icon}
    </button>
  );
}


const inputCls =
  "w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:outline-none";
